import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CourseInviteApi } from '../../services/api/CourseInviteApi';
import '../../parent/pages/ParentInviteAccept.css';

/**
 * Student-facing page for responding to a course invitation. Reached from the
 * invite email link (/course-invite/accept?token=...). Requires the student to
 * be authenticated (ProtectedRoute); the backend additionally verifies the
 * logged-in email matches the invited email.
 */
function CourseInviteAccept() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');

  const [status, setStatus] = useState('loading'); // loading | valid | invalid | accepted | declined | error
  const [courseName, setCourseName] = useState('');
  const [inviterName, setInviterName] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!token) { setStatus('invalid'); return; }
      try {
        const resp = await CourseInviteApi.validateInvite(token);
        const data = await resp.json().catch(() => ({}));
        if (!active) return;
        if (resp.ok && data.valid) {
          setCourseName(data.courseName || 'a course');
          setInviterName(data.inviterName || 'A teacher');
          setStatus('valid');
        } else {
          setStatus('invalid');
        }
      } catch (e) {
        if (active) setStatus('error');
      }
    })();
    return () => { active = false; };
  }, [token]);

  const handleAccept = useCallback(async () => {
    setSubmitting(true);
    setError('');
    try {
      const resp = await CourseInviteApi.acceptInvite(token);
      const data = await resp.json().catch(() => ({}));
      if (resp.ok) {
        setStatus('accepted');
      } else {
        setError(data.error || 'Could not accept the invitation.');
      }
    } catch (e) {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [token]);

  const handleDecline = useCallback(async () => {
    setSubmitting(true);
    setError('');
    try {
      const resp = await CourseInviteApi.declineInvite(token);
      if (resp.ok) {
        setStatus('declined');
      } else {
        const data = await resp.json().catch(() => ({}));
        setError(data.error || 'Could not decline the invitation.');
      }
    } catch (e) {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [token]);

  return (
    <div className="parent-invite-accept">
      <div className="parent-invite-accept__card">
        {status === 'loading' && <p>Checking your invitation…</p>}

        {status === 'invalid' && (
          <>
            <h2>Invitation not found</h2>
            <p>This invitation link is invalid, expired, or has already been used.</p>
            <button className="parent-invite-accept__btn" onClick={() => navigate('/dashboard')}>
              Go to my courses
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <h2>Something went wrong</h2>
            <p>We couldn't load this invitation. Please try again later.</p>
          </>
        )}

        {status === 'valid' && (
          <>
            <h2>Course invitation</h2>
            <p><strong>{inviterName}</strong> invited you to join <strong>{courseName}</strong> on DocuHounds.</p>
            <p className="parent-invite-accept__note">
              If you accept, you'll see the course schedule, assignments, and study tools.
            </p>
            {error && <p className="parent-invite-accept__error">{error}</p>}
            <div className="parent-invite-accept__actions">
              <button
                className="parent-invite-accept__btn parent-invite-accept__btn--primary"
                onClick={handleAccept}
                disabled={submitting}
              >
                {submitting ? 'Please wait…' : 'Accept'}
              </button>
              <button
                className="parent-invite-accept__btn parent-invite-accept__btn--ghost"
                onClick={handleDecline}
                disabled={submitting}
              >
                Decline
              </button>
            </div>
          </>
        )}

        {status === 'accepted' && (
          <>
            <h2>You're in!</h2>
            <p>You've joined <strong>{courseName}</strong>. It's now in your course list.</p>
            <button className="parent-invite-accept__btn" onClick={() => navigate('/dashboard')}>
              Go to my courses
            </button>
          </>
        )}

        {status === 'declined' && (
          <>
            <h2>Invitation declined</h2>
            <p>No problem. You can close this page.</p>
            <button className="parent-invite-accept__btn" onClick={() => navigate('/dashboard')}>
              Go to my courses
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default CourseInviteAccept;
